import { Link } from "react-router-dom";
import { ArrowRight, Search, Sparkles, DollarSign, MapPin, BookOpen, Heart, Wind, Zap } from "lucide-react";

const games = [
  { icon: Heart, badge: "Dog Speed Dating", title: "Swipe your way to your type", to: "/speed-dating" },
  { icon: Wind, badge: "Tuesday Fur-Meter", title: "How much fur can you live with?", to: "/fur-meter" },
  { icon: Zap, badge: "Dog Chaos Meter", title: "How much energy can you handle?", to: "/chaos-meter" },
];

const features = [
  { icon: Sparkles, title: "Lifestyle quiz", desc: "8 questions. Get your ranked breed matches in minutes.", to: "/quiz" },
  { icon: Search, title: "Breed browser", desc: "Filter by size, energy, allergies, apartment life & more.", to: "/breeds" },
  { icon: DollarSign, title: "Cost calculator", desc: "See true monthly + first-year cost by breed and city.", to: "/breeds" },
  { icon: MapPin, title: "Adopt near you", desc: "Real dogs waiting at shelters within driving distance.", to: "/adoption" },
  { icon: BookOpen, title: "First-week guides", desc: "House training, vet schedule, feeding — the real basics.", to: "/guides" },
];

const trainingPicks = [
  { slug: "how-dogs-learn", title: "How dogs actually learn", desc: "The four quadrants, explained simply.", read: 8 },
  { slug: "loose-lead-walking", title: "Stop the pulling on-lead", desc: "A step-by-step loose-lead plan.", read: 6 },
  { slug: "reliable-recall", title: "Build a rock-solid recall", desc: "Get a come-when-called that holds.", read: 6 },
];

export default function Landing() {
  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 reveal">
            <span
              data-testid="hero-tagline"
              className="inline-block px-3 py-1 rounded-full bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider"
            >
              Australia's dog breed matcher
            </span>
            <h1 className="mt-6 font-display font-bold text-accent text-5xl md:text-6xl leading-[0.95]">
              Find the dog breed that <em className="text-primary not-italic">fits your life</em>.
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-lg leading-relaxed">
              Answer a few quick questions about your home, schedule and budget. We'll show you the breeds that actually suit you — no sign-up needed.
            </p>
            <div className="mt-8">
              <Link
                to="/quiz"
                data-testid="hero-quiz-btn"
                className="pill-btn inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-8 py-4 text-lg font-semibold soft-shadow"
              >
                Take the free quiz <ArrowRight size={20} />
              </Link>
              <p className="mt-4 text-sm text-muted-foreground">
                Takes 3 minutes · Free · 100% anonymous
              </p>
              <Link
                to="/guides?category=training"
                data-testid="hero-training-teaser"
                className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-accent/80 hover:text-primary transition-colors"
              >
                <BookOpen size={15} />
                New here? Start with dog training basics
                <ArrowRight size={14} />
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative rounded-[2rem] overflow-hidden warm-shadow aspect-[4/5] max-h-[620px] grain">
              <img
                src="https://images.unsplash.com/photo-1613928521908-24ccf17a1707?w=1200"
                alt="Cavalier King Charles Spaniel"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="hidden md:flex absolute -left-6 bottom-10 float-slow items-center gap-3 bg-card rounded-2xl px-4 py-3 warm-shadow border border-border/60">
              <span className="w-10 h-10 rounded-full bg-primary/15 text-primary grid place-items-center">
                <Sparkles size={18} />
              </span>
              <div className="text-sm">
                <div className="font-semibold text-accent">96% match</div>
                <div className="text-muted-foreground text-xs">
                  Cavalier King Charles
                </div>
              </div>
            </div>
            <div className="hidden md:block absolute -right-6 top-10 bg-secondary rounded-2xl px-4 py-3 warm-shadow border border-border/60 max-w-[220px]">
              <div className="text-xs uppercase tracking-wider text-secondary-foreground/70">Est. cost to keep the dog</div>
              <div className="font-display font-bold text-2xl text-accent">From $220 AUD/mo</div>
              <div className="text-xs text-secondary-foreground/70">food, vet &amp; care — small breed, regional area. Purchase price not included.</div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16" data-testid="how-it-works">
        <div className="grid sm:grid-cols-3 gap-6">
          {[
            { n: "1", title: "Answer the quiz", desc: "A few quick questions about your home, time and budget." },
            { n: "2", title: "Get your matches", desc: "We rank the breeds that actually suit your lifestyle." },
            { n: "3", title: "Explore & decide", desc: "See costs, care needs and where to adopt or buy." },
          ].map((s) => (
            <div key={s.n} className="rounded-3xl bg-card border border-border/60 p-7 warm-shadow">
              <span className="w-10 h-10 rounded-full bg-primary text-primary-foreground grid place-items-center font-display font-bold">
                {s.n}
              </span>
              <h3 className="mt-4 font-display font-bold text-xl text-accent">{s.title}</h3>
              <p className="mt-2 text-muted-foreground text-sm">{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* POPULAR TRAINING PICKS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="training-picks-section">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-primary font-semibold uppercase tracking-wider text-xs">Popular training picks</p>
            <h2 className="mt-2 font-display font-bold text-3xl md:text-4xl text-accent">
              New owner? Start here
            </h2>
            <p className="mt-2 text-muted-foreground max-w-xl">
              The training basics every first-time owner should read — no jargon, just what works.
            </p>
          </div>
          <Link
            to="/guides?category=training"
            data-testid="training-picks-all"
            className="pill-btn inline-flex items-center gap-1.5 rounded-full border-2 border-accent text-accent px-5 py-2.5 text-sm font-semibold hover:bg-accent hover:text-accent-foreground"
          >
            All training guides <ArrowRight size={15} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-3 gap-6">
          {trainingPicks.map((g) => (
            <Link
              key={g.slug}
              to={`/guides/${g.slug}`}
              data-testid={`training-pick-${g.slug}`}
              className="group rounded-3xl bg-card border border-border/60 p-7 warm-shadow hover:-translate-y-1 transition-transform duration-300 flex flex-col justify-between min-h-[200px]"
            >
              <div>
                <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary grid place-items-center mb-4">
                  <BookOpen size={20} strokeWidth={2.2} />
                </div>
                <h3 className="font-display font-bold text-xl text-accent leading-tight">{g.title}</h3>
                <p className="mt-2 text-muted-foreground text-sm">{g.desc}</p>
              </div>
              <div className="mt-5 flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{g.read} min read</span>
                <span className="text-primary font-semibold inline-flex items-center gap-1">
                  Read <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* BENTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <h2 className="font-display font-bold text-3xl md:text-4xl text-accent mb-8">
          Explore more
        </h2>
        <div className="grid md:grid-cols-6 gap-6">
          {features.map((f, i) => (
            <Link
              key={f.title}
              to={f.to}
              data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
              className={`group rounded-3xl bg-card border border-border/60 p-8 warm-shadow hover:-translate-y-1 transition-transform duration-300 ${
                i <= 1 ? "md:col-span-3" : "md:col-span-2"
              }`}
            >
              <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary grid place-items-center mb-5">
                <f.icon size={22} strokeWidth={2.2} />
              </div>
              <h3 className="font-display font-bold text-2xl text-accent">
                {f.title}
              </h3>
              <p className="mt-2 text-muted-foreground">{f.desc}</p>
              <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                Explore <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* JUST FOR FUN — mini games */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="fun-games-section">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="text-primary font-semibold uppercase tracking-wider text-xs">Just for fun 🐾</p>
            <h2 className="mt-2 font-display font-bold text-3xl md:text-4xl text-accent">
              Not ready for the full quiz? Play a 10-second game
            </h2>
            <p className="mt-2 text-muted-foreground max-w-xl">
              Quick, playful, and shareable — a fun way to discover your dog type before the real thing.
            </p>
          </div>
          <Link
            to="/play"
            data-testid="fun-games-all"
            className="pill-btn inline-flex items-center gap-1.5 rounded-full border-2 border-accent text-accent px-5 py-2.5 text-sm font-semibold hover:bg-accent hover:text-accent-foreground"
          >
            See all games <ArrowRight size={15} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-3 gap-6">
          {games.map((g) => (
            <Link
              key={g.to}
              to={g.to}
              data-testid={`fun-game-${g.to.replace("/", "")}`}
              className="group rounded-3xl bg-card border border-border/60 p-7 warm-shadow hover:-translate-y-1 transition-transform duration-300"
            >
              <span className="inline-flex items-center gap-2 rounded-full bg-accent text-accent-foreground px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest">
                <g.icon size={12} /> {g.badge}
              </span>
              <h3 className="mt-4 font-display font-bold text-xl text-accent leading-tight">{g.title}</h3>
              <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                Play now <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="rounded-[2rem] bg-accent text-accent-foreground p-10 md:p-14 relative overflow-hidden">
          <div className="relative z-10 max-w-2xl">
            <h2 className="font-display text-4xl md:text-5xl font-bold leading-tight">
              Not sure where to start? Ask us anything.
            </h2>
            <p className="mt-4 text-accent-foreground/80 text-lg">
              Our AI advisor answers real questions — &ldquo;I work 9 hours, want a small dog that&apos;s ok alone&rdquo; — with honest guidance.
            </p>
            <p className="mt-6 text-sm text-accent-foreground/60">
              Tap the chat bubble in the corner to start.
            </p>
          </div>
          <div className="absolute right-0 bottom-0 w-72 h-72 rounded-full bg-primary/30 blur-3xl" />
        </div>
      </section>
    </div>
  );
}
